import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Checkbox, FormControlLabel, Box, CircularProgress, Autocomplete, ListItem, Typography, ListItemText, TextField, Paper, Button, Snackbar } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import TennantService from "services/TennantService";
import { useAuth } from "context/authContext";
import ImagePicker from 'components/MDImagePicker';
import CreateTennantDialog from './components/CreateTennantDialog';
import SearchAutocomplete from 'components/SearchAutocomplete'

export default function TabTennant() {
    const { token, user, isAuthenticated } = useAuth();
    const [isEdit, setIsEdit] = useState(false);
    const [reload, setReload] = useState(false);
    const [tennants, setTennants] = useState([]);
    const [updatedTennant, setUpdatedTennent] = useState({});
    const [tennant, setTennant] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTennantLoading, setSearchTennantLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({ type: '', message: '' });
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedTennant, setSelectedTennant] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        try {
            setLoading(true);
            const init = async () => {
                const authenticated = await isAuthenticated();
                if (!authenticated) {
                    navigate(`/login`);
                    return;
                }

                await loadData();
            };

            init();
        } catch (error) {
            console.error("Erro ao carregar Tab Tennant:", error);
            navigate(`/login`);
        }
    }, [navigate, user, reload]);

    const loadData = async () => {
        try {
            const data = await TennantService.GetTennants(token, null, { page: 1, pageSize: 150 });
            const tennantsList = data.data;
            setTennants(tennantsList);
            setSelectedTennant(tennantsList[0] ?? {});
            setUpdatedTennent(tennantsList[0] ?? {});
        } catch (error) {
            console.error('Erro ao carregar tennants', error);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        } finally {
            setLoading(false);
        }
    };

    const clearStates = () => {
        setUpdatedTennent({});
    }

    const onClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpen(false);
    };

    const onSelectChange = (value) => {
        const selectedTennant = tennants.find((t) => t.Id === value);
        setUpdatedTennent(selectedTennant);
        setIsEdit(false);
    }

    const onChange = (name, value) => {
        setUpdatedTennent((prev) => ({
            ...prev,
            [name]: value,
        }));
        setIsEdit(true);
    };

    const handleCreate = (name, value) => {
        setTennant((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const onCancel = () => {
        onSelectChange(updatedTennant.Id);
    }

    const onDelete = async () => {
        try {
            await TennantService.DeleteTennant(token, updatedTennant.Id);
            setAlert({ type: 'success', message: 'Empresa excluída com sucesso.' });
            setOpen(true);
            clearStates();
        } catch (error) {
            setAlert({ type: 'error', message: `Erro ao excluir empresa ${error.response?.data?.error[0]}` });
            setOpen(true);
            console.error('Erro ao excluir tennant', error);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        } finally {
            setReload(!reload);
        }
    }

    const onSave = async () => {
        try {
            const { Id, createdAt, updatedAt, ...savingData } = updatedTennant;
            await TennantService.UpdateTennant(token, Id, savingData);
            setAlert({ type: 'success', message: 'Dados salvos com sucesso.' });
            setOpen(true);
            setIsEdit(false);
            setReload(!reload);
        } catch (error) {
            setAlert({ type: 'error', message: `Erro ao salvar dados ${error.response?.data?.error[0]}` });
            setOpen(true);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        } finally {

        }
    }

    const handleCreateCancel = () => {
        setOpenDialog(false)
        setTennant({})
    }

    const onSaveCreate = async () => {
        try {
            await TennantService.CreateTennant(token, tennant);
            setAlert({ type: 'success', message: 'Empresa criada com sucesso!' });
            setOpen(true);
            setIsEdit(false);
            setReload(!reload);
            handleCreateCancel();
        } catch (error) {
            setAlert({ type: 'error', message: `Erro ao criar empresa ${error.response?.data?.error[0]}` });
            setOpen(true);
            console.log('Status:', error.response?.status);
            console.log('Resposta da API:', error.response?.data);
            console.log('Request:', error.config?.data);
        }
    }

    const onTennantSearch = async (value) => {
        try {
            setSearchTennantLoading(true);
            const response = await TennantService.GetTennants(token, null, { page: 1, pageSize: 20, find: value === '' ? null : value });
            setTennants(response.data);
        } catch (error) {
            console.error(error);
            setTennants([]);
        } finally {
            setSearchTennantLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
            </Box>
        );
    }

    const snackBar = (
        <Snackbar open={open} autoHideDuration={3000} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <MuiAlert onClose={onClose} severity={alert.type} sx={{ width: "100%" }}>
                {alert.message}
            </MuiAlert>
        </Snackbar>
    )

    const modalCreateTennant = (
        <CreateTennantDialog
            open={openDialog}
            tennant={tennant}
            onChange={handleCreate}
            onCancel={handleCreateCancel}
            onConfirm={onSaveCreate}
        />
    )

    return (
        <MDBox mt={2}>
            <Paper
                elevation={3}
                sx={{
                    backgroundColor: '#fff',
                    padding: 2,
                    borderRadius: 3,
                    width: '100%',
                }}
            >
                <Box
                    mt={2}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                    }}
                >
                    <SearchAutocomplete
                        label="Empresa"
                        options={tennants}
                        loading={searchTennantLoading}
                        value={selectedTennant}
                        getOptionLabel={(tennant) =>
                            tennant
                                ? `${tennant.Id} - ${tennant.CompanyName}`
                                : ''
                        }
                        onSearch={onTennantSearch}
                        onChange={(tennant) => {
                            setSelectedTennant(tennant)
                            onSelectChange(tennant?.Id ?? '')
                        }}
                    />
                    <MDButton variant="contained" color="success" size="medium" onClick={() => setOpenDialog(true)}>
                        Criar
                    </MDButton>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        width: '100%',
                        mt: 2,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <TextField
                            fullWidth
                            label="Nome"
                            type="text"
                            variant="outlined"
                            value={updatedTennant?.CompanyName || ''}
                            onChange={(e) => onChange('CompanyName', e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="CNPJ"
                            type="text"
                            variant="outlined"
                            value={updatedTennant?.MainTaxId || ''}
                            onChange={(e) => onChange('MainTaxId', e.target.value)}
                        />
                    </Box>
                    <Box
                        sx={{
                            width: 160,
                            flexShrink: 0,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <ImagePicker
                            value={updatedTennant?.Base64Logo}
                            onChange={(base64) => onChange('Base64Logo', base64)}
                            size={140}
                        />
                    </Box>
                </Box>

                <span style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                    Configurações de envio de e-mail:
                </span>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                        mb: 2,
                        mt: 1,
                    }}
                >
                    <TextField
                        fullWidth
                        label="E-mail"
                        type="email"
                        variant="outlined"
                        value={updatedTennant?.EmailUser || ''}
                        onChange={(e) => onChange('EmailUser', e.target.value)}
                    />

                    <TextField
                        fullWidth
                        label="Senha"
                        type="password"
                        variant="outlined"
                        value={updatedTennant?.EmailPassword || '*********'}
                        onChange={(e) => onChange('EmailPassword', e.target.value)}
                    />
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                    }}
                >
                    <TextField
                        fullWidth
                        label="E-mail From"
                        type="email"
                        variant="outlined"
                        value={updatedTennant?.EmailFrom || ''}
                        onChange={(e) => onChange('EmailFrom', e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Host"
                        type="text"
                        variant="outlined"
                        value={updatedTennant?.EmailHost || ''}
                        onChange={(e) => onChange('EmailHost', e.target.value)}
                    />
                    <TextField
                        fullWidth
                        label="Porta"
                        type="number"
                        variant="outlined"
                        value={updatedTennant?.EmailPort || ''}
                        onChange={(e) => onChange('EmailPort', e.target.value)}
                    />
                    <FormControlLabel
                        label="Secure"
                        labelPlacement='start'
                        sx={{ width: "40%" }}
                        control={
                            <Checkbox
                                checked={updatedTennant?.EmailSecure || false}
                                onChange={(e) => onChange('EmailSecure', e.target.checked)}
                            />
                        } />
                </Box>

                <Box
                    mt={2}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        width: "100%",
                    }}
                >
                    <MDButton variant="contained" color="success" size="medium" disabled={!isEdit} onClick={onSave}>
                        Salvar
                    </MDButton>
                    <MDButton variant="contained" color="info" size="medium" disabled={!isEdit} onClick={onCancel}>
                        Cancelar
                    </MDButton>
                    <MDButton variant="contained" color="error" size="medium" disabled={updatedTennant?.Id === undefined} onClick={onDelete}>
                        Excluir
                    </MDButton>
                </Box>
                {snackBar}
                {modalCreateTennant}
            </Paper>
        </MDBox>
    )
}